import React from 'react';
import dayjs from 'dayjs';
import { Alert, Box, Button, CircularProgress, IconButton, Tooltip, Typography } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import MilestoneProgressBar from './ProgressMilestonesComponent';
import { describeSlip, parseMilestoneDate } from './procoreDateSlip';
import { procoreProjectAdminUrl } from './procoreLinks';
import procoreLogo from '../../static/procore_logo.png';

const HEADING_ID = 'procore-stage-dates-heading';

const PROCORE_DATE_FIELDS = [
	['start_date', 'Start Date'],
	['actual_start_date', 'Actual Start Date'],
	['completion_date', 'Completion Date'],
	['projected_finish_date', 'Projected Finish Date'],
];

const PROJECTED_FINISH_NOTE = 'Projected Finish Date is not the TCO date — it should track anticipated substantial completion.';

// The dashboard bar positions every milestone against actual start → projected finish
// and draws nothing (or stacks labels) when one of the four is missing or out of order.
const barCanDraw = (project) => {
	const [start, actualStart, completion, projectedFinish] = PROCORE_DATE_FIELDS.map(([field]) => parseMilestoneDate(project?.[field]));
	return Boolean(start && actualStart && completion && projectedFinish && projectedFinish > actualStart);
};

// Always in the DOM so screen readers get the dates; shown on screen only when the bar can't draw them.
const ProcoreDateList = ({ project, visible }) => (
	<Box
		component="ul"
		aria-label="Procore dates"
		sx={visible ? { listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 0.5 } : visuallyHidden}
	>
		{PROCORE_DATE_FIELDS.filter(([field]) => project?.[field]).map(([field, label]) => (
			<li key={field}>
				<Box component="span" sx={{ color: 'text.secondary' }}>{label}:</Box>{' '}
				<Box component="span" sx={{ fontWeight: 700 }}>{project[field]}</Box>
			</li>
		))}
	</Box>
);

export const formatConfirmedAt = (iso) => dayjs(iso).format('MM/DD/YYYY h:mm a');

const ProcoreTimelineCard = ({ project }) => {
	const notSet = [
		...(project?.stage ? [] : ['Stage']),
		...PROCORE_DATE_FIELDS.filter(([field]) => !project?.[field]).map(([, label]) => label),
	];
	const slip = describeSlip(project?.completion_date, project?.projected_finish_date);
	const showBar = barCanDraw(project);

	return (
		<Box
			component="section"
			aria-labelledby={HEADING_ID}
			sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, mb: 2 }}
		>
			{project?.stage && (
				<Typography component="p" sx={{ mb: 2, fontSize: 18, lineHeight: '24px' }}>
					<Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>Stage:</Box>{' '}
					<Box component="span" sx={{ fontWeight: 700 }}>{project.stage}</Box>
				</Typography>
			)}
			{showBar && (
				// The bar's milestone labels hang above and below it, outside its own box.
				<Box aria-hidden="true" data-testid="procore-milestones" sx={{ display: 'flex', justifyContent: 'center', pb: 6 }}>
					<MilestoneProgressBar projectData={{ project_data: project }} />
				</Box>
			)}
			<ProcoreDateList project={project} visible={!showBar} />
			{slip && (
				<Typography variant="body2" sx={{ mt: 1.5, fontWeight: 600, color: 'warning.dark' }}>
					{slip}
				</Typography>
			)}
			{notSet.length > 0 && (
				<Box sx={{ mt: 1.5 }}>
					<Typography variant="caption" component="p" sx={{ color: 'text.secondary', fontWeight: 600 }}>
						Not set in Procore
					</Typography>
					<Box component="ul" aria-label="Not set in Procore" sx={{ m: 0, pl: 2.5, color: 'text.secondary', fontSize: '0.8rem' }}>
						{notSet.map((label) => <li key={label}>{label}</li>)}
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default function ProcoreStageDatesStep({
	project,
	confirmation,
	confirmed,
	confirming,
	refreshing,
	error,
	onConfirm,
	onRefresh,
	onDismissError,
}) {
	const procoreId = project?.procore_id;

	return (
		<Box>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
				<Typography id={HEADING_ID} variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
					Confirm
					<img src={procoreLogo} alt="" width={20} height={20} />
					Procore Stage & Dates
				</Typography>
				<Tooltip title={PROJECTED_FINISH_NOTE} describeChild arrow enterTouchDelay={0} leaveTouchDelay={4000}>
					<IconButton size="small" aria-label="About Projected Finish Date" sx={{ p: '2px' }}>
						<InfoOutlinedIcon sx={{ fontSize: 18 }} />
					</IconButton>
				</Tooltip>
			</Box>
			{procoreId ? (
				<ProcoreTimelineCard project={project} />
			) : (
				<Alert severity="info" sx={{ mb: 2 }}>
					This job isn't linked to Procore. You can confirm and continue.
				</Alert>
			)}

			{confirmed && confirmation?.confirmed_at && (
				<Typography role="status" variant="body2" sx={{ color: 'success.dark', mb: 2 }}>
					Confirmed by {confirmation.confirmed_by || 'an unknown user'} on {formatConfirmedAt(confirmation.confirmed_at)}
				</Typography>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }} onClose={onDismissError}>
					{error}
				</Alert>
			)}

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 2 }}>
				<Button
					variant="outlined"
					onClick={onRefresh}
					disabled={refreshing}
					startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
				>
					Refresh
				</Button>
				{procoreId && (
					<Button
						variant="outlined"
						component="a"
						href={procoreProjectAdminUrl(procoreId)}
						target="_blank"
						rel="noopener noreferrer"
						endIcon={<OpenInNewIcon />}
					>
						Open in Procore
					</Button>
				)}
				<Button
					variant={confirmed ? 'outlined' : 'contained'}
					onClick={onConfirm}
					disabled={confirming}
				>
					{confirmed ? 'Confirmed' : 'Confirm'}
				</Button>
			</Box>
		</Box>
	);
}
